<template>
    <div class="sources-view-wrap">
        <v-card class="rounded_card quaroshadow mt-4 mb-4 sources-chart-shell" style="border-top: 4px solid var(--v-primary-base); overflow: hidden" outlined>
            <v-toolbar id="projects-tool-bar" flat class="gradient-toolbar elevation-0 sources-chart-toolbar">
                <div class="project-data-heading d-flex align-center src-section-head">
                    <h2 class="project-data-heading__title">Page Type Distribution (URLs)</h2>
                    <v-tooltip top max-width="320">
                        <template v-slot:activator="{ on, attrs }">
                            <v-icon small class="src-help-icon" v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
                        </template>
                        <span>Distribution of classified page types based on source URLs in the currently filtered result set.</span>
                    </v-tooltip>
                </div>
                <div class="flex-grow-1"></div>
            </v-toolbar>
            <v-card-text class="pt-3">
                <div class="">
                    <v-row dense>
                        <v-col cols="12" md="7">
                            <div v-if="pageTypeStats.length" class="page-type-chart-card">
                                <div class="page-type-chart-card__meta-wrap">
                                    <div class="page-type-chart-card__meta">{{ totalPageTypeUrls }} total URLs</div>
                                    <v-tooltip left max-width="280">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-icon x-small class="src-help-icon" v-bind="attrs" v-on="on">mdi-help-circle-outline</v-icon>
                                        </template>
                                        <span>Total number of URLs included in the page type classification.</span>
                                    </v-tooltip>
                                </div>
                                <div class="page-type-chart-wrap">
                                    <div class="page-type-chart-backdrop" aria-hidden="true"></div>
                                    <div class="page-type-chart-canvas-box">
                                        <sources-pie-chart :chartData="pageTypeChartData" :options="pageTypeChartOptions" :reloadPieChart="reloadPieChart"></sources-pie-chart>
                                    </div>
                                </div>
                            </div>
                            <div v-else class="src-kpi__hint">No page type data available</div>
                        </v-col>
                        <v-col cols="12" md="5">
                            <div v-if="pageTypeStats.length" class="src-type-list">
                                <div
                                    v-for="item in pageTypeStats"
                                    :key="`pt-${item.typeKey}`"
                                    class="src-type-item src-type-item--clickable"
                                    :class="{ 'src-type-item--active': selectedPageTypeFilter === item.typeKey }"
                                    @click="onPageTypeItemClick(item)">
                                    <div class="src-type-item__top">
                                        <div class="src-type-item__left">
                                            <span class="src-type-item__dot" :style="{ backgroundColor: getPageTypeColor(item.typeKey) }"></span>
                                            <div class="src-type-item__name-wrap">
                                                <div class="src-type-item__name-line">
                                                    <span class="src-type-item__name">{{ item.typeLabel }}</span>
                                                    <v-tooltip top max-width="260">
                                                        <template v-slot:activator="{ on, attrs }">
                                                            <v-icon x-small class="src-help-icon" v-bind="attrs" v-on="on">mdi-help-circle-outline</v-icon>
                                                        </template>
                                                        <span>{{ item.typeLabel }}: {{ item.count }} URLs ({{ item.pct.toFixed(1) }}%)</span>
                                                    </v-tooltip>
                                                </div>
                                                <span class="src-type-item__meta">Share {{ item.pct.toFixed(1) }}%</span>
                                            </div>
                                        </div>
                                        <div class="src-type-item__right">
                                            <span class="src-type-item__value">{{ item.count }} URLs</span>
                                            <span class="src-type-item__pct-chip">{{ item.pct.toFixed(1) }}%</span>
                                        </div>
                                    </div>
                                    <div class="src-type-item__bar">
                                        <div class="src-type-item__bar-fill" :style="{ width: item.pct + '%', backgroundColor: getPageTypeColor(item.typeKey) }"></div>
                                    </div>
                                </div>
                            </div>
                        </v-col>
                    </v-row>
                </div>
            </v-card-text>
        </v-card>

        <v-card ref="sourcesTableCard" class="rounded_card my-5 quaroshadow" style="border-top: 4px solid var(--v-primary-base); overflow: hidden">
            <v-toolbar id="projects-tool-bar" flat class="gradient-toolbar elevation-0">
                <v-toolbar-items class="sources-toolbarbtns mr-3">
                    <v-btn text :class="{ 'active-button': tableMode === 'url' }" :color="tableMode === 'url' ? 'primary darken-1' : 'grey darken-4'" @click="setTableMode('url')">
                        <v-icon x-small class="mr-1">mdi-link-variant</v-icon>
                        URLs
                    </v-btn>
                    <v-btn text :class="{ 'active-button': tableMode === 'domain' }" :color="tableMode === 'domain' ? 'primary darken-1' : 'grey darken-4'" @click="setTableMode('domain')">
                        <v-icon x-small class="mr-1">mdi-web</v-icon>
                        Domains
                    </v-btn>
                </v-toolbar-items>
                <div class="flex-grow-1"></div>
                <v-text-field
                    class="pr-5"
                    v-model="localSearch"
                    rounded
                    dense
                    filled
                    clearable
                    clear-icon="mdi-close"
                    prepend-inner-icon="mdi-magnify"
                    label="Search..."
                    single-line
                    hide-details
                    aria-label="Search table"
                    @input="onSearchInput" />
                <v-btn rounded color="primary" filled :loading="exporting" :disabled="exporting" @click="exportLocalTable" aria-label="Export table">
                    <v-icon aria-hidden="true">mdi-cloud-download</v-icon>
                </v-btn>
            </v-toolbar>
            <div v-if="selectedPageTypeFilter" class="sources-filter-reset-row px-4 py-2">
                <v-btn small outlined color="primary" class="sources-filter-reset-btn" @click="clearPageTypeFilter">
                    <v-icon x-small class="mr-1">mdi-close</v-icon>
                    Reset type filter: {{ getPageTypeLabel(selectedPageTypeFilter) }}
                </v-btn>
            </div>
            <v-card-text class="px-0">
                <SourcesTable
                    ref="table"
                    :isActive="true"
                    :project_id="projectId"
                    :selectedDates="selectedDates"
                    :search="localSearch"
                    :modeValue="tableMode"
                    :pageTypeFilter="selectedPageTypeFilter"
                    :tagsOperator="tagsOperator"
                    :tags="tags"
                    :project="project"
                    :googlePromptCount="googlePromptCount"
                    :selectedPlatform="selectedPlatform"
                    :importDatesByPlatform="importDatesByPlatform"
                    :exportData="0"
                    @update:mode="onTableModeChanged"
                    @overviewUpdated="onOverviewUpdated" />
            </v-card-text>
        </v-card>
    </div>
</template>

<script>
    import SourcesTable from "../../../components/aisuite/SourcesTable.vue"
    import SourcesPieChart from "../../../components/charts/SourcesPieChart"

    const PAGE_TYPE_LABELS = {
        blog_guide: "Blog Guide",
        product_service_page: "Product / Service Page",
        category_hub: "Category Hub",
        homepage: "Homepage",
        article: "Article",
        news: "News",
        review: "Review",
        comparison: "Comparison",
        faq: "FAQ",
        docs: "Documentation",
        forum: "Forum",
        video: "Video",
        unknown: "Unknown"
    }

    const PAGE_TYPE_COLORS = {
        blog_guide: "#60A5FA",
        product_service_page: "#FBBF24",
        category_hub: "#4ADE80",
        homepage: "#A78BFA",
        comparison_page: "#F472B6",
        recommendation_list: "#22D3EE",
        use_case_editorial: "#34D399",
        directory_profile: "#F59E0B",
        review_platform: "#2DD4BF",
        docs_help_api: "#94A3B8",
        company_trust_page: "#10B981",
        news_press_editorial: "#F87171",
        community_ugc: "#FB923C",
        legal_utility: "#A3A3A3",
        unknown_other: "#CBD5E1",
        article: "#38BDF8",
        news: "#F87171",
        review: "#2DD4BF",
        comparison: "#F472B6",
        faq: "#C084FC",
        docs: "#94A3B8",
        forum: "#FB923C",
        video: "#FB7185",
        unknown: "#CBD5E1"
    }

    export default {
        name: "AIProjectSourcesView",
        components: { SourcesTable, SourcesPieChart },
        props: {
            projectId: { type: [String, Number], default: "" },
            selectedDates: { type: Array, default: () => [] },
            search: { type: String, default: "" },
            tagsOperator: { type: String, default: "or" },
            tags: { type: Array, default: () => [] },
            project: { type: Object, default: null },
            googlePromptCount: { type: Number, default: 0 },
            selectedPlatform: { type: [Object, String], default: null },
            importDatesByPlatform: { type: Array, default: () => [] },
            cachedPageTypeSignature: { type: String, default: "" },
            cachedPageTypeStatsRaw: { type: Array, default: () => [] }
        },
        data() {
            return {
                overview: {
                    winners: [],
                    losers: [],
                    top5Share: 0,
                    totalResponses: 0,
                    mode: "domain",
                    rows: []
                },
                pageTypeStatsRaw: [],
                pageTypeReqSeq: 0,
                reloadPieChart: 0,
                localSearch: "",
                tableMode: "url",
                selectedPageTypeFilter: null,
                exporting: false,
                pageTypeChartOptions: {
                    responsive: true,
                    maintainAspectRatio: true,
                    aspectRatio: 1,
                    layout: {
                        padding: {
                            top: 8,
                            right: 8,
                            bottom: 8,
                            left: 8
                        }
                    },
                    animation: {
                        duration: 550,
                        easing: "easeOutQuart"
                    },
                    elements: {
                        arc: {
                            borderWidth: 0,
                            borderColor: "transparent"
                        }
                    },
                    legend: {
                        display: false,
                        position: "bottom",
                        labels: {
                            boxWidth: 12,
                            fontSize: 11
                        }
                    },
                    tooltips: {
                        backgroundColor: "rgba(15, 23, 42, 0.92)",
                        titleFontSize: 12,
                        bodyFontSize: 12,
                        xPadding: 10,
                        yPadding: 10,
                        cornerRadius: 8,
                        displayColors: false,
                        callbacks: {
                            label: (tooltipItem, data) => {
                                const labels = data?.labels || []
                                const datasets = data?.datasets || []
                                const ds = datasets[tooltipItem.datasetIndex] || {}
                                const values = Array.isArray(ds.data) ? ds.data : []
                                const total = values.reduce((sum, v) => sum + (Number(v) || 0), 0)
                                const val = Number(values[tooltipItem.index] || 0)
                                const pct = total > 0 ? ((val / total) * 100).toFixed(1) : "0.0"
                                return `${labels[tooltipItem.index]}: ${val} URLs (${pct}%)`
                            }
                        }
                    }
                }
            }
        },
        computed: {
            pageTypeInputSignature() {
                try {
                    return JSON.stringify({
                        projectId: this.projectId,
                        selectedDates: this.selectedDates,
                        selectedPlatform: this.selectedPlatform,
                        tags: this.tags,
                        tagsOperator: this.tagsOperator,
                        importDatesByPlatform: this.importDatesByPlatform
                    })
                } catch (e) {
                    return ""
                }
            },
            pageTypeSliceGapColor() {
                return this.$vuetify && this.$vuetify.theme && this.$vuetify.theme.dark ? "#1f2328" : "#f8fafc"
            },
            totalPageTypeUrls() {
                return this.pageTypeStats.reduce((sum, item) => sum + (Number(item?.count) || 0), 0)
            },
            pageTypeStats() {
                const rows = Array.isArray(this.pageTypeStatsRaw) ? this.pageTypeStatsRaw : []
                if (!rows.length) return []

                const map = new Map()
                rows.forEach((row) => {
                    const keyRaw = row?.page_type
                    const key = keyRaw && String(keyRaw).trim() ? String(keyRaw).trim().toLowerCase() : "unknown"
                    map.set(key, (map.get(key) || 0) + (Number(row?.count) || 0))
                })

                const total = Array.from(map.values()).reduce((sum, v) => sum + v, 0)
                return Array.from(map.entries())
                    .map(([typeKey, count]) => ({
                        typeKey,
                        typeLabel: this.getPageTypeLabel(typeKey),
                        count,
                        pct: total > 0 ? (count / total) * 100 : 0
                    }))
                    .sort((a, b) => b.count - a.count)
            },
            pageTypeChartData() {
                const labels = this.pageTypeStats.map((x) => x.typeLabel)
                const data = this.pageTypeStats.map((x) => x.count)
                const colors = this.pageTypeStats.map((x) => this.getPageTypeColor(x.typeKey))
                const gapColor = this.pageTypeSliceGapColor
                const sliceBorders = Array(Math.max(data.length, 1)).fill(gapColor)
                return {
                    labels,
                    datasets: [
                        {
                            backgroundColor: colors,
                            borderColor: sliceBorders,
                            borderWidth: 6,
                            hoverBorderColor: sliceBorders,
                            hoverBorderWidth: 8,
                            data
                        }
                    ]
                }
            }
        },
        watch: {
            search: {
                immediate: true,
                handler(val) {
                    this.localSearch = val || ""
                }
            },
            selectedDates: {
                deep: true,
                handler() {
                    this.fetchPageTypeRows()
                }
            },
            selectedPlatform: {
                deep: true,
                handler() {
                    this.fetchPageTypeRows()
                }
            },
            tags: {
                deep: true,
                handler() {
                    this.fetchPageTypeRows()
                }
            },
            tagsOperator() {
                this.fetchPageTypeRows()
            },
            importDatesByPlatform: {
                deep: true,
                handler() {
                    this.fetchPageTypeRows()
                }
            },
            pageTypeChartData: {
                deep: true,
                handler() {
                    this.reloadPieChart += 1
                }
            }
        },
        methods: {
            getPageTypeLabel(typeKey) {
                const key = (typeKey || "unknown").toString().trim().toLowerCase()
                if (PAGE_TYPE_LABELS[key]) return PAGE_TYPE_LABELS[key]
                return key.replace(/_/g, " ").replace(/\b\w/g, (ch) => ch.toUpperCase())
            },
            getPageTypeColor(typeKey) {
                const key = (typeKey || "unknown").toString().trim().toLowerCase()
                return PAGE_TYPE_COLORS[key] || PAGE_TYPE_COLORS.unknown
            },
            onSearchInput() {
                this.$emit("update:search", this.localSearch)
            },
            schedulePageTypeRowsRefresh() {
                clearTimeout(this._pageTypeDebounce)
                this._pageTypeDebounce = setTimeout(() => this.fetchPageTypeRows(), 400)
            },
            setTableMode(nextMode) {
                if (nextMode !== "domain" && nextMode !== "url") return
                this.tableMode = nextMode
            },
            onPageTypeItemClick(item) {
                const key = item && item.typeKey ? item.typeKey : null
                if (!key) return
                this.tableMode = "url"
                this.selectedPageTypeFilter = key
                this.scrollToSourcesTable()
            },
            clearPageTypeFilter() {
                this.selectedPageTypeFilter = null
            },
            scrollToSourcesTable() {
                this.$nextTick(() => {
                    const tableCard = this.$refs.sourcesTableCard
                    const el = tableCard && tableCard.$el ? tableCard.$el : tableCard
                    if (el && typeof el.scrollIntoView === "function") {
                        el.scrollIntoView({ behavior: "smooth", block: "start" })
                    }
                })
            },
            onTableModeChanged(nextMode) {
                if (nextMode !== "domain" && nextMode !== "url") return
                this.tableMode = nextMode
            },
            async fetchPageTypeRows() {
                const reqSeq = ++this.pageTypeReqSeq
                if (!this.selectedDates || !this.selectedDates.length) {
                    this.pageTypeStatsRaw = []
                    return
                }

                const endpointBase = "/ai-suite/sources/page-type-stats/get"
                const baseUrl =
                    (JSON.parse(process.env.VUE_APP_MONITORING_PROXY_COMPANY_IDS).indexOf(
                        this.CryptoJS.AES.decrypt(localStorage.company_id, process.env.VUE_APP_ENCRYPTION_PW).toString(this.CryptoJS.enc.Utf8)
                    ) == -1
                        ? process.env.VUE_APP_MONITORING_API_URL
                        : process.env.VUE_APP_APIURL + "/monitoring-api") + endpointBase

                const dates = [...this.selectedDates].sort()
                const oldest = dates[0]
                const newest = dates[dates.length - 1]

                const baseCommon = {
                    company_id: localStorage.company_id,
                    project_id: this.projectId || this.$route.params.id
                }
                if (this.tags && this.tags.length) {
                    baseCommon.tags = this.tags
                    baseCommon.tagsOperator = this.tagsOperator
                }

                const isAll = this.selectedPlatform?.key === "all"

                try {
                    if (isAll) {
                        if (!Array.isArray(this.importDatesByPlatform)) {
                            this.pageTypeStatsRaw = []
                            return
                        }
                        const resp = await this.$helpers.axiosPost(baseUrl, {
                            ...baseCommon,
                            platform: null,
                            importDatesByPlatform: this.importDatesByPlatform
                        })
                        if (reqSeq !== this.pageTypeReqSeq) return
                        this.pageTypeStatsRaw = Array.isArray(resp.data?.stats) ? resp.data.stats : []
                        this.$emit("pageTypeLoaded", {
                            signature: this.pageTypeInputSignature,
                            statsRaw: this.pageTypeStatsRaw
                        })
                        return
                    }

                    const resp = await this.$helpers.axiosPost(baseUrl, {
                        ...baseCommon,
                        selectedDates: [newest || oldest],
                        platform: this.selectedPlatform?.key
                    })
                    if (reqSeq !== this.pageTypeReqSeq) return
                    this.pageTypeStatsRaw = Array.isArray(resp.data?.stats) ? resp.data.stats : []
                    this.$emit("pageTypeLoaded", {
                        signature: this.pageTypeInputSignature,
                        statsRaw: this.pageTypeStatsRaw
                    })
                } catch (e) {
                    if (reqSeq !== this.pageTypeReqSeq) return
                    this.pageTypeStatsRaw = []
                }
            },
            async exportLocalTable() {
                if (this.exporting) return
                this.exporting = true
                try {
                    const csv = await this.exportTableData()
                    if (!csv) return
                    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" })
                    const link = document.createElement("a")
                    link.href = URL.createObjectURL(blob)
                    link.download = `sources_export_${new Date().toISOString().slice(0, 10)}.csv`
                    document.body.appendChild(link)
                    link.click()
                    document.body.removeChild(link)
                    URL.revokeObjectURL(link.href)
                } finally {
                    this.exporting = false
                }
            },
            onOverviewUpdated(payload) {
                const safe = payload && typeof payload === "object" ? payload : {}
                this.overview = {
                    winners: Array.isArray(safe.winners) ? safe.winners : [],
                    losers: Array.isArray(safe.losers) ? safe.losers : [],
                    top5Share: Number.isFinite(Number(safe.top5Share)) ? Number(safe.top5Share) : 0,
                    totalResponses: Number.isFinite(Number(safe.totalResponses)) ? Number(safe.totalResponses) : 0,
                    mode: safe.mode || "domain",
                    rows: Array.isArray(safe.rows) ? safe.rows : []
                }
                if (safe.mode === "domain" || safe.mode === "url") {
                    this.tableMode = safe.mode
                }
            },
            formatPct(value) {
                const n = Number(value)
                if (!Number.isFinite(n)) return "0.0%"
                return `${n.toFixed(1)}%`
            },
            async exportTableData() {
                const table = this.$refs.table
                if (table && typeof table.exportTableData === "function") {
                    return table.exportTableData()
                }
                return ""
            }
        },
        mounted() {
            if (this.cachedPageTypeSignature && this.cachedPageTypeSignature === this.pageTypeInputSignature && Array.isArray(this.cachedPageTypeStatsRaw) && this.cachedPageTypeStatsRaw.length) {
                this.pageTypeStatsRaw = [...this.cachedPageTypeStatsRaw]
                return
            }
            this.fetchPageTypeRows()
        }
    }
</script>

<style scoped>
    .sources-view-wrap {
        --src-card-radius: 14px;
        --src-page-type-panel-height: 372px;
    }
    .src-kpi {
        border: 1px solid rgba(15, 23, 42, 0.09);
        border-radius: var(--src-card-radius);
        background: #fff;
        box-shadow: 0 10px 24px -24px rgba(15, 23, 42, 0.45);
        padding: 12px;
        min-height: 118px;
        position: relative;
        overflow: hidden;
    }
    .src-kpi__label {
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.4px;
        text-transform: uppercase;
        color: rgba(15, 23, 42, 0.62);
        margin-bottom: 6px;
    }
    .src-kpi__value {
        font-size: 32px;
        font-weight: 700;
        line-height: 1.1;
        margin-bottom: 4px;
        color: #1f2937;
    }
    .src-kpi__hint {
        font-size: 12px;
        color: rgba(55, 65, 81, 0.75);
    }
    .src-kpi__line {
        height: 2px;
        border-radius: 999px;
        margin-top: 10px;
        background: linear-gradient(90deg, rgba(16, 185, 129, 0.35), rgba(34, 197, 94, 0.85));
    }
    .src-kpi__label--with-help {
        display: inline-flex;
        align-items: center;
        gap: 6px;
    }
    .src-help-icon {
        color: rgba(100, 116, 139, 0.78);
        cursor: help;
        transition: color 0.15s ease;
    }
    .src-help-icon:hover {
        color: var(--v-primary-base);
    }
    .src-section-head {
        display: inline-flex;
        align-items: center;
        gap: 8px;
    }
    .sources-chart-shell {
        border-radius: var(--src-card-radius);
    }
    .sources-chart-toolbar {
        border-radius: 14px 14px 0 0;
    }
    .sources-toolbarbtns .v-btn {
        text-transform: none;
        letter-spacing: 0;
        font-weight: 600;
        border-radius: 10px !important;
        margin: 4px 2px;
    }
    .sources-filter-reset-row {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        border-bottom: 1px solid rgba(15, 23, 42, 0.08);
    }
    .theme--dark .sources-filter-reset-row {
        border-bottom-color: rgba(255, 255, 255, 0.1);
    }
    .sources-filter-reset-btn {
        text-transform: none;
        letter-spacing: 0.1px;
        font-weight: 600;
    }
    .src-section-head .project-data-heading__title {
        margin: 0;
        font-size: 0.85rem;
        font-weight: 600;
        letter-spacing: 0.2px;
        color: rgba(15, 23, 42, 0.72);
        text-transform: uppercase;
    }
    .src-list {
        display: grid;
        gap: 8px;
    }
    .src-list__row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
    }
    .src-list__row--card {
        border: 1px solid rgba(15, 23, 42, 0.1);
        border-radius: 12px;
        background: #fff;
        padding: 8px 10px;
        box-shadow: none;
    }
    .src-list__left {
        display: inline-flex;
        align-items: center;
        min-width: 0;
        gap: 8px;
    }
    .src-list__meta-wrap {
        display: flex;
        flex-direction: column;
        min-width: 0;
        line-height: 1.2;
    }
    .src-list__dot {
        width: 10px;
        height: 10px;
        border-radius: 999px;
        flex: 0 0 auto;
    }
    .src-list__dot.up {
        background: #22c55e;
    }
    .src-list__dot.down {
        background: #ef4444;
    }
    .src-list__name {
        font-size: 14px;
        font-weight: 600;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    .src-list__meta {
        font-size: 11px;
        color: rgba(100, 116, 139, 0.9);
        margin-top: 1px;
    }
    .src-list__delta {
        font-size: 18px;
        font-weight: 700;
        white-space: nowrap;
        padding: 4px 10px;
        border-radius: 999px;
        line-height: 1;
        font-variant-numeric: tabular-nums;
    }
    .src-list__delta.up {
        color: #15803d;
        background: rgba(22, 163, 74, 0.16);
    }
    .src-list__delta.down {
        color: #b91c1c;
        background: rgba(239, 68, 68, 0.15);
    }
    .src-type-list {
        display: grid;
        gap: 8px;
    }
    .src-type-section {
        border: 1px solid rgba(15, 23, 42, 0.08);
        border-radius: 12px;
        background: linear-gradient(180deg, rgba(255, 255, 255, 0.7), rgba(249, 250, 251, 0.85));
        padding: 10px;
    }
    .page-type-chart-card {
        border: 1px solid rgba(15, 23, 42, 0.08);
        border-radius: var(--src-card-radius);
        /* background: linear-gradient(180deg, #ffffff, #f8fafc); */
        padding: 8px 8px 2px;
        box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.75), 0 8px 22px -18px rgba(15, 23, 42, 0.4);
        overflow: hidden;
    }
    .page-type-chart-card__meta-wrap {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        float: right;
        padding-right: 6px;
    }
    .page-type-chart-card__meta {
        font-size: 11px;
        font-weight: 600;
        letter-spacing: 0.2px;
        color: rgba(55, 65, 81, 0.8);
        text-align: right;
    }
    .src-type-item {
        border: 1px solid rgba(15, 23, 42, 0.08);
        border-radius: 10px;
        background: #fff;
        padding: 8px 10px;
        box-shadow: none;
        border-left: 3px solid rgba(15, 23, 42, 0.08);
    }
    .src-type-item--clickable {
        cursor: pointer;
        transition: transform 0.14s ease, box-shadow 0.16s ease, border-color 0.16s ease, background 0.16s ease;
    }
    .src-type-item--clickable:hover {
        transform: translateY(-1px);
        box-shadow: 0 8px 18px -14px rgba(15, 23, 42, 0.45);
        border-color: rgba(59, 130, 246, 0.35);
    }
    .src-type-item--active {
        border-color: rgba(59, 130, 246, 0.45);
        box-shadow: 0 10px 20px -16px rgba(59, 130, 246, 0.55);
        background: rgba(59, 130, 246, 0.06);
    }
    .src-type-item__top {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        margin-bottom: 6px;
    }
    .src-type-item__left {
        display: inline-flex;
        align-items: center;
        min-width: 0;
        gap: 8px;
    }
    .src-type-item__name-wrap {
        display: flex;
        flex-direction: column;
        min-width: 0;
        line-height: 1.2;
    }
    .src-type-item__name-line {
        display: inline-flex;
        align-items: center;
        gap: 6px;
    }
    .src-type-item__dot {
        width: 10px;
        height: 10px;
        border-radius: 999px;
        flex: 0 0 10px;
        box-shadow: 0 0 0 2px #fff, 0 0 0 3px rgba(0, 0, 0, 0.08);
        position: relative;
        top: 1px;
    }
    .src-type-item__name {
        font-size: 14px;
        font-weight: 600;
        color: #333b46;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 180px;
    }
    .src-type-item__meta {
        font-size: 11px;
        color: #595959;
        font-weight: 500;
        letter-spacing: 0.1px;
        margin-top: 1px;
    }
    .src-type-item__right {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        flex-shrink: 0;
    }
    .src-type-item__value {
        font-size: 16px;
        font-weight: 700;
        line-height: 1;
        color: #1f2937;
        white-space: nowrap;
    }
    .src-type-item__pct-chip {
        display: inline-flex;
        align-items: center;
        font-size: 12px;
        font-weight: 700;
        border-radius: 999px;
        padding: 2px 8px;
        background: rgba(107, 114, 128, 0.14);
        color: #6b7280;
        font-variant-numeric: tabular-nums;
        line-height: 1;
    }
    .src-type-item__bar {
        width: 100%;
        height: 7px;
        border-radius: 999px;
        background: rgba(15, 23, 42, 0.08);
        overflow: hidden;
    }
    .src-type-item__bar-fill {
        height: 100%;
        border-radius: 999px;
        transition: width 0.35s ease;
    }
    .src-type-row {
        border: 1px solid rgba(15, 23, 42, 0.08);
        border-radius: 10px;
        padding: 8px;
        background: rgba(255, 255, 255, 0.55);
    }
    .src-type-left,
    .src-type-right {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
    }
    .src-type-name {
        font-size: 12px;
        font-weight: 700;
    }
    .src-type-meta {
        font-size: 11px;
        color: rgba(55, 65, 81, 0.72);
    }
    .src-type-share {
        font-size: 12px;
        font-weight: 700;
    }
    .src-type-delta {
        font-size: 11px;
        font-weight: 700;
        display: inline-flex;
        align-items: center;
        gap: 2px;
    }
    .src-type-delta.up {
        color: #1f9d45;
    }
    .src-type-delta.down {
        color: #d64545;
    }
    .src-type-bar {
        margin-top: 6px;
        width: 100%;
        height: 7px;
        border-radius: 999px;
        background: rgba(15, 23, 42, 0.08);
        overflow: hidden;
    }
    .src-type-bar-fill {
        height: 100%;
        border-radius: 999px;
        background: linear-gradient(90deg, var(--v-primary-base), color-mix(in srgb, var(--v-primary-base) 65%, #ffffff));
    }
    .page-type-chart-wrap {
        /* min-height: 240px;
        height: 260px; */
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
        min-height: 340px;
    }
    @media (min-width: 960px) {
        .page-type-chart-card {
            height: var(--src-page-type-panel-height);
            display: flex;
            flex-direction: column;
        }
        .page-type-chart-wrap {
            min-height: 0;
            height: calc(var(--src-page-type-panel-height) - 30px);
            flex: 1 1 auto;
        }
        .src-type-list {
            max-height: var(--src-page-type-panel-height);
            overflow-y: auto;
            padding-right: 4px;
        }
    }
    .page-type-chart-backdrop {
        position: absolute;
        width: 230px;
        height: 230px;
        border-radius: 999px;
        background: radial-gradient(circle at 50% 50%, rgba(37, 99, 235, 0.12), rgba(245, 158, 11, 0.08) 45%, rgba(22, 163, 74, 0.06) 72%, transparent 100%);
        filter: blur(0.4px);
        pointer-events: none;
    }
    .page-type-chart-canvas-box {
        width: min(100%, 340px);
        height: min(100%, 340px);
        max-width: 100%;
        max-height: 100%;
        position: relative;
        z-index: 1;
    }
    .page-type-chart-canvas-box >>> canvas {
        width: 100% !important;
        height: 100% !important;
        max-width: 100% !important;
        max-height: 100% !important;
        display: block !important;
    }
    .theme--dark .src-kpi {
        background: #1f2328;
        border-color: rgba(148, 163, 184, 0.24);
        box-shadow: 0 14px 26px -22px rgba(0, 0, 0, 0.75);
    }
    .theme--dark .src-kpi__value {
        color: #f1f5f9;
    }
    .theme--dark .src-kpi__hint {
        color: rgba(203, 213, 225, 0.78);
    }
    .theme--dark .src-section-head .project-data-heading__title {
        color: rgba(226, 232, 240, 0.86);
    }
    .theme--dark .page-type-chart-card,
    .theme--dark .src-type-item {
        background: #242a31;
        border-color: rgba(148, 163, 184, 0.2);
    }

    .theme--dark .page-type-chart-backdrop {
        background: radial-gradient(circle at 50% 50%, rgba(37, 99, 235, 0.24), rgba(245, 158, 11, 0.17) 45%, rgba(22, 163, 74, 0.13) 72%, transparent 100%);
    }
    .theme--dark .src-type-item__name {
        color: #e6ebf2;
    }
    .theme--dark .src-type-item__meta {
        color: #9ca7b5;
    }
    .theme--dark .src-type-item__value {
        color: #f3f6fb;
    }
    .theme--dark .src-type-item__pct-chip {
        background: #343a40;
        color: #ccd3d9;
    }
    .theme--dark .src-type-item__dot {
        box-shadow: 0 0 0 2px #1f1f1f, 0 0 0 3px rgba(255, 255, 255, 0.08);
    }
    .theme--dark .src-type-item__bar {
        background: rgba(148, 163, 184, 0.2);
    }
</style>
